import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import { DotsPattern } from '../../../assets/svgs/DotsPattern';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { hapticsImpactLight } from '../../../utils/haptics';
import Select from '@mui/material/Select';
import {
  renderAccommodations,
  useHandleContentTypeChange,
} from '../../../utils/sessionUtils';
import { BlockType } from '../../../types/Study';
import MenuItem from '@mui/material/MenuItem';
import { TriangleRight } from '../../../assets/svgs/TriangleRight';
import { Minus, Plus, CheckCircle } from 'react-feather';
import Tooltip from '@mui/material/Tooltip';
import OraSwitch from '../../CustomMUI/Switch';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import { useAccess } from '../../../hooks/useAccess';
import { useStudyPhaseSend } from '../../../studyPhase';
import { updateCurrentUserInfo } from '../../../services/auth';
import { enqueueSnackbar } from 'notistack';
import {
  selectActiveBlock,
  selectAllBlocksSubmitted,
  selectUnstartedBlocks,
} from '../../../slices/blocks/blocksSlice';
import { updateBlock } from '../../../services/blocks';
import { useNavigate } from 'react-router-dom';

interface ContentTypeProps {
  type: 'qbank' | 'flashcards' | 'video';
  currentCount: number;
  totalCount: number;
  minCount: number;
  maxCount: number;
  isEditingCustomSession: boolean;
  setIsEditingCustomSession: (isEditing: boolean) => void;
  customCount: number;
  setCustomCount: (count: number) => void;
}

const ContentType: React.FC<ContentTypeProps> = ({
  type,
  currentCount,
  totalCount,
  minCount,
  maxCount,
  isEditingCustomSession,
  setIsEditingCustomSession,
  customCount,
  setCustomCount,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const activeBlock = useSelector(selectActiveBlock);
  const unstartedBlocks = useSelector(selectUnstartedBlocks);
  const allBlocksSubmitted = useSelector(selectAllBlocksSubmitted);

  const { sendPhaseEvent } = useStudyPhaseSend();
  const { isGated } = useAccess();
  const navigate = useNavigate();

  const handleContentTypeChange = useHandleContentTypeChange(
    currentUser,
    primaryExam
  );

  const handleSpinClick = (
    direction: 'up' | 'down',
    currentValue: number,
    min: number,
    max: number
  ) => {
    if (direction === 'up') {
      if (currentValue + 1 > max) {
        setCustomCount(max);
      } else {
        setCustomCount(currentValue + 1);
      }
    } else {
      if (currentValue - 1 < min) {
        setCustomCount(min);
      } else {
        setCustomCount(currentValue - 1);
      }
    }
  };

  const renderPlusTooltip = () => {
    if (type === 'flashcards') {
      return customCount >= 500
        ? 'Flashcard ount cannot be greater than 500.'
        : '';
    } else if (type === 'qbank') {
      return customCount >= 320
        ? 'QBank count cannot be greater than 320.'
        : '';
    }
  };

  const renderMinusTooltip = () => {
    if (type === 'flashcards') {
      return customCount === 0
        ? ''
        : customCount <= minCount
          ? 'Flashcard count cannot be less than the number of completed flashcards.'
          : '';
    } else if (type === 'qbank') {
      return customCount === 0
        ? ''
        : customCount <= minCount
          ? 'QBank count cannot be less than the number of questions in started or submitted blocks.'
          : '';
    }
  };

  const renderContentTypeChangeTooltip = () => {
    if (type === 'qbank') {
      return primaryExam?.isUsingQbank && !primaryExam?.isUsingFlashcards
        ? 'At least one content type must be enabled. Flashcards will be enabled if you turn off QBank.'
        : '';
    } else if (type === 'flashcards') {
      return primaryExam?.isUsingFlashcards && !primaryExam?.isUsingQbank
        ? 'At least one content type must be enabled. QBank will be enabled if you turn off Flashcards.'
        : '';
    }
  };

  const handleBlockTypeChange = async (blockType: BlockType) => {
    if (!activeBlock) {
      return;
    }
    await updateCurrentUserInfo({
      preferredBlockType: blockType,
    });
    for (const block of unstartedBlocks) {
      await updateBlock({
        blockId: block.id,
        type: blockType,
        duration: blockType === BlockType.tutor ? 0 : block.vignetteCount * 90,
      });
    }
    let messageText = '';
    if (activeBlock?.isStarted) {
      messageText =
        'Block type updated. This change will take effect on the next block.';
    } else {
      messageText = 'Block type updated.';
    }
    enqueueSnackbar(messageText, { autoHideDuration: 5000 });
  };

  const startSession = () => {
    hapticsImpactLight();
    if (type === 'qbank') {
      sendPhaseEvent({ type: 'START_QBANK' });
    } else if (type === 'flashcards') {
      sendPhaseEvent({ type: 'START_FLASHCARDS' });
    } else if (type === 'video') {
      sendPhaseEvent({ type: 'START_VIDEOS' });
    }
  };

  const typeLabel = () => {
    if (primaryExam?.isRCT) {
      return 'Assessment';
    }
    if (type === 'qbank') {
      return 'QBank';
    } else if (type === 'flashcards') {
      return 'Flashcards';
    } else if (type === 'video') {
      return 'Videos';
    }
  };

  return (
    <div
      className={`content-type content-type--${type}${isEditingCustomSession ? ' is-editing' : ''}${
        (type === 'qbank' && !primaryExam?.isUsingQbank) ||
        (type === 'flashcards' && !primaryExam?.isUsingFlashcards)
          ? ' is-inactive'
          : ''
      }`}
    >
      {isEditingCustomSession && (
        <div className="content-type_switch">
          <Tooltip title={renderContentTypeChangeTooltip()} enterTouchDelay={0}>
            <span>
              <OraSwitch
                checked={
                  type === 'qbank'
                    ? primaryExam?.isUsingQbank
                    : primaryExam?.isUsingFlashcards
                }
                onClick={() => {
                  hapticsImpactLight();
                  handleContentTypeChange(
                    type === 'qbank' ? 'qbank' : 'flashcard'
                  );
                }}
                tabIndex={0}
                aria-label={`Toggle ${type} content type`}
              />
            </span>
          </Tooltip>
        </div>
      )}
      <div className="content-type_main">
        <div className="content-type_main_top">
          <div
            className="progress-container"
            onClick={() => {
              if (!isEditingCustomSession) {
                setIsEditingCustomSession(true);
                hapticsImpactLight();
              }
            }}
            tabIndex={0}
            aria-label={`Edit ${type} count`}
          >
            <CircularProgressbar
              value={Math.round((currentCount / totalCount) * 100)}
              strokeWidth={7}
              styles={buildStyles({
                pathTransitionDuration: 1,
                pathColor:
                  type === 'qbank'
                    ? 'var(--ct-qbank)'
                    : type === 'flashcards'
                      ? 'var(--ct-flashcards)'
                      : 'var(--ct-videos)',
                textColor: 'var(--type-heading)',
                trailColor: 'var(--surface-tertiary)',
              })}
            />
            {isEditingCustomSession ? (
              <Tooltip
                title={
                  type === 'qbank' && isGated()
                    ? 'Upgrade to modify QBank count'
                    : ''
                }
                enterTouchDelay={0}
              >
                <input
                  type="number"
                  className="progress-text-input"
                  value={customCount}
                  min={minCount}
                  max={maxCount}
                  onChange={(e) => {
                    setCustomCount(Number(e.target.value));
                  }}
                  tabIndex={0}
                  disabled={type === 'qbank' && isGated()}
                />
              </Tooltip>
            ) : (
              <div className="progress-text">
                {isGated() && type === 'qbank' ? (
                  <Tooltip
                    title="Your current plan gives you 3 daily QBank for this exam."
                    enterTouchDelay={0}
                  >
                    <span
                      style={{
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                      }}
                    >
                      {totalCount?.toString() || ''}
                    </span>
                  </Tooltip>
                ) : (
                  totalCount?.toString() || ''
                )}
              </div>
            )}
            {isEditingCustomSession && !(type === 'qbank' && isGated()) && (
              <div className="spinners">
                <Tooltip title={renderPlusTooltip()} enterTouchDelay={0}>
                  <span>
                    <button
                      className="button button--secondary"
                      onClick={() =>
                        handleSpinClick('up', customCount, minCount, maxCount)
                      }
                      tabIndex={0}
                      aria-label={`Increase ${type} count by 1`}
                      disabled={
                        (type === 'flashcards' && customCount >= 500) ||
                        (type === 'qbank' && customCount >= 320)
                      }
                    >
                      <Plus />
                    </button>
                  </span>
                </Tooltip>
                <Tooltip title={renderMinusTooltip()} enterTouchDelay={0}>
                  <span>
                    <button
                      className="button button--secondary"
                      onClick={() =>
                        handleSpinClick('down', customCount, minCount, maxCount)
                      }
                      tabIndex={0}
                      aria-label={`Decrease ${type} count by 1`}
                      disabled={customCount <= minCount}
                    >
                      <Minus />
                    </button>
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
          <h2 className="content-type_main_top_title">
            {primaryExam?.isRCT ? primaryExam?.name : typeLabel()}
          </h2>
          <p className="content-type_main_top_progress">
            {`${currentCount} of ${totalCount} ${
              type === 'flashcards'
                ? 'Completed'
                : type === 'video'
                  ? 'Watched'
                  : activeBlock?.type === BlockType.timed
                    ? 'Answered'
                    : 'Completed'
            }`}
          </p>
          {currentUser && type === 'qbank' && (
            <div className="content-type_main_top_block-type">
              <Select
                labelId="block-type-selector-label"
                id="block-type-selector"
                value={activeBlock?.type || ''}
                disabled={primaryExam?.isRCT}
                onChange={(e) => {
                  const newValue = e.target.value as BlockType;
                  if (newValue === BlockType.timed && isGated()) {
                    navigate('/pricing');
                    hapticsImpactLight();
                    return;
                  }
                  handleBlockTypeChange(newValue);
                }}
                aria-label={`Select QBank block type`}
                onClick={hapticsImpactLight}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
              >
                <MenuItem value="tutor">
                  <TriangleRight />
                  Untimed
                </MenuItem>
                <MenuItem value="timed" sx={isGated() ? { opacity: 0.5 } : {}}>
                  <TriangleRight />
                  {isGated() ? 'Timed (Upgrade)' : 'Timed'}
                </MenuItem>
              </Select>
              {renderAccommodations(currentUser)}
            </div>
          )}
        </div>

        <div className="content-type_main_bottom">
          <Tooltip
            title={
              isEditingCustomSession
                ? 'Save changes or cancel to continue.'
                : ''
            }
            enterTouchDelay={0}
          >
            <span>
              <button
                className="button button--start"
                onClick={startSession}
                disabled={
                  isEditingCustomSession ||
                  customCount === 0 ||
                  (currentCount === totalCount && type === 'flashcards')
                }
                tabIndex={0}
                aria-label={`Start ${type} session`}
              >
                {currentCount === totalCount ||
                (type === 'qbank' && allBlocksSubmitted) ? (
                  type === 'qbank' ? (
                    'Review QBank'
                  ) : (
                    <CheckCircle />
                  )
                ) : (
                  `${currentCount === 0 ? 'Start' : 'Continue'} ${typeLabel()}`
                )}
              </button>
            </span>
          </Tooltip>
        </div>
      </div>
      <div
        className={`content-type-highlight content-type-highlight--${type}`}
      ></div>
      <DotsPattern />
    </div>
  );
};

export default ContentType;
